import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ConditionalMessage, LookupObject } from '@wdx/shared/utils';

@Injectable({
    providedIn: 'any',
})
export class FormFieldsDataService {
    messageDefinitionToShow: { [key: string]: ConditionalMessage } = {};
    messageDefinitionToObject: { [key: string]: ConditionalMessage } = {};

    /**
     * Parses form data and flattens formgroups with empty data to null (if required)
     * - eg. if a FormGroup has a value of {a: null, b:null} but backend expects a null value returned
     * - if no parsed fields are defined, then simply return the raw form value
     */
    parseFormData(fieldsToNullCheck: string[], formGroup: FormGroup) {
        const transformedData = this.transformData(formGroup.getRawValue());

        if (fieldsToNullCheck.length) {
            return Object.entries(transformedData).reduce(
                (previous, [key, parsedValue]) => {
                    if (
                        fieldsToNullCheck.includes(key) &&
                        parsedValue !== null &&
                        parsedValue !== undefined &&
                        typeof parsedValue === 'object' &&
                        // Check for null, undefined, or empty string
                        Object.values(parsedValue).every(
                            (val) =>
                                val === undefined ||
                                val === null ||
                                val.length === 0,
                        )
                    ) {
                        parsedValue = null;
                    }

                    return { ...previous, [key]: parsedValue };
                },
                {},
            );
        }

        return transformedData;
    }

    /**
     * Add a field name to list of fields to null check
     */
    addFieldsToNullCheck(fieldsToNullCheck: string[], fieldName: string) {
        if (!fieldsToNullCheck.includes(fieldName)) {
            fieldsToNullCheck.push(fieldName);
        }
    }

    transformData = (data: any): any => {
        if (!data || typeof data !== 'object') {
            return data;
        }

        if (Array.isArray(data)) {
            return data.map(this.transformData);
        }

        if (data.lookupType) {
            return {
                id: data.id,
                lookupType: data.lookupType,
            };
        }

        return Object.fromEntries(
            Object.entries(data).map(([key, value]) => [
                key,
                this.transformData(value),
            ]),
        );
    };
}
